import { bonusStatuses, millisecondsInDay } from 'common/constants';
import {
  flattenClientBonusInstance,
  getEffectiveTimeToWager,
} from 'common/utils/flattenClientBonusInstance';

import {
  formatFinishedBonusDetailsDate,
  formatWagerDisplay,
  statusDateLabelKey,
} from './finishedBonusDetailsShared';

const { EXPIRED, SPEND } = bonusStatuses;

const resolvePeriodDays = bonus => {
  const tw = getEffectiveTimeToWager(bonus);

  if (tw == null) {
    return null;
  }

  return Math.max(0, Math.floor(Number(tw) / millisecondsInDay));
};

// Mirrors normalizeEpochMs from Bonuses/shared/bonusTimeFormat, inlined so this
// popup doesn't import that module (it pulls the @atlas/bonuses SDK barrel into
// the unit tests). Coerces seconds-or-ms API timestamps to epoch ms.
const normalizeEpochMs = raw => {
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : new Date(raw).getTime();
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n < 1e12 ? n * 1000 : n;
};

// EXPIRED shows when the instance went Expired. Matches the bonus card / detail
// (resolveExpiredEndMs): the BE nulls expirationTime once Expired, so the moment
// is derived from the wager window (wagerStartTime + timeToWager, always present),
// falling back to expirationTime / endTime - FT-8580.
const resolveStatusDateMs = row => {
  if (row == null) {
    return null;
  }

  const wagerStart = normalizeEpochMs(row.wagerStartTime);
  const timeToWager = row.timeToWager;
  if (wagerStart != null && Number.isFinite(timeToWager) && timeToWager > 0) {
    return wagerStart + timeToWager;
  }

  return normalizeEpochMs(row.expirationTime) ?? normalizeEpochMs(row.endTime);
};

// Search doesn't include payout amounts yet, so we skip extra money rows. SPEND still shows a red "spent" label until billing is ready
export const buildFinishedClientBonusDetailsRows = ({ bonus }) => {
  const b = flattenClientBonusInstance(bonus) ?? bonus;
  const status = b?.status;
  const rows = [];

  const productValueKey =
    b?.productType === 'SPORTSBOOK' ? 'profile_bonuses_sportsbook' : 'profile_bonuses_casino';

  rows.push({
    labelKey: 'product',
    valueLabelKey: productValueKey,
  });

  const statusDateMs = resolveStatusDateMs(b);

  if (status === EXPIRED && statusDateMs != null) {
    rows.push({
      labelKey: statusDateLabelKey(status),
      valueText: formatFinishedBonusDetailsDate(statusDateMs),
    });
  }

  const wager = b?.wager ?? b?.copiedBonusSettings?.wager;

  rows.push({
    labelKey: 'bonus_info_wager',
    valueName: 'wager',
    valueText: formatWagerDisplay(wager),
  });

  const days = resolvePeriodDays(b);

  if (days != null) {
    rows.push({
      labelKey: 'finished_bonuses_period',
      valueDays: days,
    });
  }

  if (status === SPEND) {
    rows.push({
      labelKey: 'finished_bonuses_result',
      valueLabelKey: 'finished_bonuses_result_value_spend',
      valueTone: 'danger',
    });
  }

  return rows;
};
